import { useState } from 'react';
import { Plus } from 'lucide-react';

import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useForumPublish } from '@/hooks/useForum';
import { genId } from '@/lib/forum';
import { useToast } from '@/hooks/useToast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

export function NewCategoryDialog({ triggerLabel = 'New category' }: { triggerLabel?: string }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [desc, setDesc] = useState('');
  const [busy, setBusy] = useState(false);
  const { user } = useCurrentUser();
  const { publishNode } = useForumPublish();
  const { toast } = useToast();

  if (!user) return null;

  async function submit() {
    if (!title.trim()) {
      toast({ title: 'A title is required' });
      return;
    }
    setBusy(true);
    try {
      await publishNode({
        id: genId('cat'),
        parent: null,
        title: title.trim(),
        content: desc.trim(),
        type: 'category',
      });
      toast({ title: 'Category created' });
      setOpen(false);
      setTitle('');
      setDesc('');
    } catch (e) {
      toast({ title: 'Could not create category', description: String(e) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-[#105B86] text-white hover:bg-[#0d4f74]">
          <Plus className="h-4 w-4" />
          {triggerLabel}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create a category</DialogTitle>
          <DialogDescription>
            Categories group related forums together.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="cat-title">Title</Label>
            <Input
              id="cat-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. General Discussion"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="cat-desc">Description (optional)</Label>
            <Textarea
              id="cat-desc"
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              placeholder="A short description of this category"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            className="bg-[#105B86] text-white hover:bg-[#0d4f74]"
            onClick={submit}
            disabled={busy}
          >
            {busy ? 'Creating…' : 'Create category'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
